import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Package, Printer, ShoppingCart, Plus, Pencil, Trash2,
  Search, ArrowLeft, Tag, DollarSign,
  User, Barcode, Camera, Users, Receipt, Calendar,
} from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { DealPriceTag } from "@/components/DealPriceTag";
import { printPriceTags } from "@/lib/printTags";
import { printReceipt } from "@/lib/printReceipt";


// ─── INVENTORY TAB ────────────────────────────────────
interface ProductForm {
  name: string;
  price: string;
  category: string;
  condition: string;
  platform: string;
  inventory_count: string;
  description: string;
  image_url: string;
  featured: boolean;
  active: boolean;
}

const emptyForm: ProductForm = {
  name: "", price: "0", category: "game", condition: "used",
  platform: "", inventory_count: "0", description: "", image_url: "",
  featured: false, active: true,
};

const InventoryTab = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: products, isLoading } = useQuery({
    queryKey: ["bo-products"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (product: ProductForm & { id?: string }) => {
      const payload = {
        name: product.name,
        price: parseFloat(product.price),
        category: product.category,
        condition: product.condition,
        platform: product.platform || null,
        inventory_count: parseInt(product.inventory_count),
        description: product.description || null,
        image_url: product.image_url || null,
        featured: product.featured,
        active: product.active,
      };
      if (product.id) {
        const { error } = await supabase.from("products").update(payload).eq("id", product.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("products").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bo-products"] });
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      toast({ title: editingId ? "Product updated" : "Product added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bo-products"] });
      toast({ title: "Product deleted" });
    },
  });

  const openEdit = (product: any) => {
    setEditingId(product.id);
    setForm({
      name: product.name,
      price: String(product.price),
      category: product.category,
      condition: product.condition,
      platform: product.platform || "",
      inventory_count: String(product.inventory_count),
      description: product.description || "",
      image_url: product.image_url || "",
      featured: product.featured,
      active: product.active,
    });
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const filtered = products?.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p as any).upc?.toLowerCase().includes(q)
    );
  });

  const handlePrintInventory = () => {
    if (!products?.length) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const totalUnits = products.reduce((s, p) => s + (p.inventory_count || 0), 0);
    const totalValue = products.reduce((s, p) => s + (Number(p.price) * (p.inventory_count || 0)), 0);

    const rows = products.map((p) => `
      <tr>
        <td>${p.name}</td>
        <td style="font-family:monospace;">${(p as any).upc || '-'}</td>
        <td>${p.platform || '-'}</td>
        <td>${p.category}</td>
        <td>${p.condition}</td>
        <td style="text-align:right;">${p.inventory_count}</td>
        <td style="text-align:right;">$${Number(p.price).toFixed(2)}</td>
        <td style="text-align:right;">$${(Number(p.price) * (p.inventory_count || 0)).toFixed(2)}</td>
      </tr>
    `).join("");

    printWindow.document.write(`
      <html><head><title>Inventory List</title>
      <style>
        body{font-family:monospace;margin:24px;color:#000;}
        h1{font-size:18px;margin:0 0 4px;}
        .meta{font-size:11px;color:#555;margin-bottom:16px;}
        table{width:100%;border-collapse:collapse;font-size:11px;}
        th,td{border:1px solid #333;padding:6px 8px;text-align:left;}
        th{background:#eee;text-transform:uppercase;font-size:10px;letter-spacing:1px;}
        tfoot td{font-weight:bold;background:#f5f5f5;}
      </style>
      </head><body>
        <h1>LYNX TO THE PAST — INVENTORY</h1>
        <div class="meta">124 Williams St, Lockland OH 45215 • Printed ${new Date().toLocaleString()}</div>
        <table>
          <thead><tr>
            <th>Product</th><th>UPC</th><th>Platform</th><th>Category</th><th>Condition</th>
            <th style="text-align:right;">Qty</th><th style="text-align:right;">Price</th><th style="text-align:right;">Value</th>
          </tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr>
            <td colspan="5">TOTALS — ${products.length} SKUs</td>
            <td style="text-align:right;">${totalUnits}</td>
            <td></td>
            <td style="text-align:right;">$${totalValue.toFixed(2)}</td>
          </tr></tfoot>
        </table>
      </body></html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or UPC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 font-mono text-sm bg-card border-border"
          />
        </div>
        <Button onClick={handlePrintInventory} size="sm" variant="outline" className="font-mono gap-1.5">
          <Printer className="w-4 h-4" /> Print
        </Button>
        <Button onClick={openAdd} size="sm" className="font-mono gap-1.5">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="font-mono text-[10px] tracking-wider">PRODUCT</TableHead>
              <TableHead className="font-mono text-[10px] tracking-wider">UPC</TableHead>
              <TableHead className="font-mono text-[10px] tracking-wider">PRICE</TableHead>
              <TableHead className="font-mono text-[10px] tracking-wider">QTY</TableHead>
              <TableHead className="font-mono text-[10px] tracking-wider">STATUS</TableHead>
              <TableHead className="font-mono text-[10px] tracking-wider w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center font-mono text-sm text-muted-foreground py-8">Loading...</TableCell></TableRow>
            ) : filtered?.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center font-mono text-sm text-muted-foreground py-8">No products found</TableCell></TableRow>
            ) : (
              filtered?.map((p) => (
                <TableRow key={p.id} className="border-border">
                  <TableCell>
                    <div>
                      <p className="font-mono text-xs font-semibold text-foreground">{p.name}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{p.platform} • {p.category}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">{(p as any).upc || '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-primary font-bold">${Number(p.price).toFixed(2)}</TableCell>
                  <TableCell className="font-mono text-xs">{p.inventory_count}</TableCell>
                  <TableCell>
                    <Badge
                      variant={p.active ? "default" : "secondary"}
                      className="text-[9px] font-mono"
                    >
                      {p.active ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteId(p.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-mono text-foreground">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-xs text-muted-foreground">
              This will permanently delete this product from inventory. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-mono">No</AlertDialogCancel>
            <AlertDialogAction
              className="font-mono bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) deleteMutation.mutate(deleteId);
                setDeleteId(null);
              }}
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-mono text-foreground">
              {editingId ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="font-mono text-sm bg-background border-border" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="font-mono text-sm bg-background border-border" />
              <Input placeholder="Quantity" type="number" value={form.inventory_count} onChange={(e) => setForm({ ...form, inventory_count: e.target.value })} className="font-mono text-sm bg-background border-border" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground">
                <option value="game">Game</option>
                <option value="console">Console</option>
                <option value="accessory">Accessory</option>
              </select>
              <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground">
                <option value="new">New</option>
                <option value="used">Used</option>
                <option value="refurbished">Refurbished</option>
              </select>
            </div>
            <Input placeholder="Platform (e.g. Nintendo 64)" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="font-mono text-sm bg-background border-border" />
            <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="font-mono text-sm bg-background border-border" />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="font-mono text-sm bg-background border-border" />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 font-mono text-xs text-foreground cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-primary" />
                Featured
              </label>
              <label className="flex items-center gap-2 font-mono text-xs text-foreground cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-primary" />
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-mono">Cancel</Button>
            <Button onClick={() => upsertMutation.mutate({ ...form, id: editingId || undefined })} className="font-mono">
              {editingId ? "Save" : "Add Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── TAG PRINT TAB ────────────────────────────────────
const TagPrintTab = () => {
  const [search, setSearch] = useState("");
  const { data: products } = useQuery({
    queryKey: ["bo-products"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").eq("active", true).order("name");
      if (error) throw error;
      return data;
    },
  });

  const [selected, setSelected] = useState<string[]>([]);

  const filtered = products?.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedProducts = (products || []).filter((p) => selected.includes(p.id));

  const toggleSelect = (id: string) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const handlePrint = () => {
    if (!selectedProducts.length) return;
    printPriceTags(
      selectedProducts.map((p) => ({ name: p.name, price: p.price, upc: p.upc })),
      "Price Tags"
    );
  };

  // Adapt a product row into the DealItem shape DealPriceTag expects.
  const toDealItem = (p: any) => ({
    id: p.id,
    name: p.name,
    platform: p.platform || "",
    imageUrl: p.image_url || "",
    photoUrl: null,
    priceType: "",
    originalPrice: String(p.price ?? "0"),
    grade: "",
    gradePrice: String(p.price ?? "0"),
    upc: p.upc || undefined,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 font-mono text-sm bg-card border-border" />
        </div>
        <Button onClick={handlePrint} disabled={selected.length === 0} size="sm" className="font-mono gap-1.5">
          <Printer className="w-4 h-4" /> Print ({selected.length})
        </Button>
      </div>

      {selectedProducts.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-3 space-y-3">
          <p className="font-mono text-[10px] tracking-wider text-muted-foreground">
            PREVIEW ({selectedProducts.length} TAG{selectedProducts.length === 1 ? "" : "S"})
          </p>
          <div className="flex flex-col items-center gap-2">
            {selectedProducts.map((p) => (
              <DealPriceTag
                key={p.id}
                item={toDealItem(p)}
                price={Number(p.price).toFixed(2)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filtered?.map((p) => (
          <button
            key={p.id}
            onClick={() => toggleSelect(p.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
              selected.includes(p.id)
                ? "border-primary bg-primary/5"
                : "border-border bg-card hover:border-primary/30"
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              selected.includes(p.id) ? "border-primary bg-primary" : "border-muted-foreground/30"
            }`}>
              {selected.includes(p.id) && <span className="text-primary-foreground text-xs">✓</span>}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="font-mono text-xs font-semibold text-foreground truncate">{p.name}</p>
              <p className="font-mono text-[10px] text-muted-foreground">{p.platform} • ${Number(p.price).toFixed(2)}</p>
              <p className={`font-mono text-[10px] ${p.upc ? "text-primary" : "text-destructive"}`}>
                UPC: {p.upc || "MISSING"}
              </p>
            </div>
            <Tag className="w-4 h-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── POS TAB ──────────────────────────────────────────
const POSTab = () => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState<{ id: string; productId?: string; name: string; price: number; qty: number; platform?: string }[]>([]);

  // Customer fields
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  // UPC scanning
  const [upcInput, setUpcInput] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [upcLoading, setUpcLoading] = useState(false);
  const [scannedItem, setScannedItem] = useState<{
    name: string; platform: string; imageUrl: string;
    prices: Record<string, string | null>;
  } | null>(null);

  const { data: products } = useQuery({
    queryKey: ["bo-products"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").eq("active", true).order("name");
      if (error) throw error;
      return data;
    },
  });

  const filtered = products?.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) && p.inventory_count > 0
  );

  const lookupUPC = async (upc: string) => {
    const code = upc.trim();
    if (!code) return;
    setUpcLoading(true);
    setScannedItem(null);
    try {
      // Normalize scanned code — scanners can prepend/append whitespace,
      // add a leading zero, or drop a leading zero. Try every variant so
      // the POS lookup matches what's stored in inventory.
      const digits = code.replace(/\D/g, "");
      const candidates = Array.from(
        new Set(
          [code, digits, digits.replace(/^0+/, ""), digits.padStart(12, "0")].filter(Boolean)
        )
      );

      // 1. Try local inventory first — exact match against any normalized variant.
      const { data: invMatches, error: invErr } = await supabase
        .from("products")
        .select("*")
        .in("upc", candidates)
        .eq("active", true)
        .limit(1);
      if (invErr) throw invErr;
      const invMatch = invMatches?.[0];

      if (invMatch) {
        addToCart(invMatch);
        toast({
          title: "Added to Cart",
          description: `${invMatch.name} — $${Number(invMatch.price).toFixed(2)}`,
        });
        setUpcInput("");
        return;
      }

      // 2. Try deal_items (UPCs attached via the /upc page)
      const { data: dealMatches, error: dealErr } = await supabase
        .from("deal_items")
        .select("id, name, platform, upc, grade_price, image_url, photo_url")
        .in("upc", candidates)
        .limit(1);
      if (dealErr) throw dealErr;
      const dealMatch = dealMatches?.[0];
      if (dealMatch) {
        const price = Number(dealMatch.grade_price || 0);
        setCart((prev) => [
          ...prev,
          {
            id: `deal-${dealMatch.id}-${Date.now()}`,
            name: dealMatch.name,
            price,
            qty: 1,
            platform: dealMatch.platform || undefined,
          },
        ]);
        toast({
          title: "Added to Cart",
          description: `${dealMatch.name} — $${price.toFixed(2)}`,
        });
        setUpcInput("");
        return;
      }

      // 3. Fall back to PriceCharting lookup
      const { data, error } = await supabase.functions.invoke("price-lookup", { body: { upc: code } });
      if (error) throw error;
      if (data?.success && data.data) {
        setScannedItem(data.data);
      } else {
        toast({ title: "Not Found", description: data?.error || "No product found for this UPC.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Lookup Error", description: err.message || "Failed to look up UPC.", variant: "destructive" });
    } finally {
      setUpcLoading(false);
    }
  };

  const handleBarcodeScan = (code: string) => {
    setUpcInput(code);
    setIsScanning(false);
    lookupUPC(code);
  };

  const addScannedToCart = (priceKey: string) => {
    if (!scannedItem) return;
    const priceVal = scannedItem.prices[priceKey];
    if (!priceVal) return;
    const price = parseFloat(priceVal.replace(/,/g, ""));
    const id = `upc-${Date.now()}`;
    setCart((prev) => [...prev, { id, name: scannedItem.name, price, qty: 1, platform: scannedItem.platform }]);
    setScannedItem(null);
    setUpcInput("");
    toast({ title: "Added to Cart", description: `${scannedItem.name} — $${price.toFixed(2)}` });
  };

  const addToCart = (product: any) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.productId === product.id);
      if (existing) {
        return prev.map((c) => c.productId === product.id ? { ...c, qty: c.qty + 1 } : c);
      }
      return [...prev, { id: product.id, productId: product.id, name: product.name, price: Number(product.price), qty: 1, platform: product.platform }];
    });
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((c) => c.id !== id));
  };

  const updateCartPrice = (id: string, price: number) => {
    setCart((prev) => prev.map((c) => c.id === id ? { ...c, price: isNaN(price) ? 0 : price } : c));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const TAX_RATE = 0.07;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    try {
      // 1. Find or create customer (if any contact info given)
      let customerId: string | null = null;
      const hasCustomer = customerName.trim() || customerEmail.trim() || customerPhone.trim();
      if (hasCustomer) {
        const emailLc = customerEmail.trim().toLowerCase();
        let existing: { id: string } | null = null;
        if (emailLc) {
          const { data } = await supabase.from("customers").select("id").ilike("email", emailLc).maybeSingle();
          if (data) existing = data;
        }
        if (!existing && customerPhone.trim()) {
          const { data } = await supabase.from("customers").select("id").eq("phone", customerPhone.trim()).maybeSingle();
          if (data) existing = data;
        }
        if (existing) {
          customerId = existing.id;
          await supabase.from("customers").update({
            name: customerName.trim() || undefined,
            email: emailLc || undefined,
            phone: customerPhone.trim() || undefined,
          }).eq("id", existing.id);
        } else {
          const { data: created, error: cErr } = await supabase.from("customers").insert({
            name: customerName.trim() || "Walk-in",
            email: emailLc || null,
            phone: customerPhone.trim() || null,
          }).select("id").single();
          if (cErr) throw cErr;
          customerId = created.id;
        }
      }

      // 2. Create invoice
      const { data: invoice, error: invErr } = await supabase.from("customer_invoices").insert({
        user_id: null,
        customer_id: customerId,
        customer_name: customerName.trim() || null,
        customer_email: customerEmail.trim() || null,
        customer_phone: customerPhone.trim() || null,
        subtotal,
        tax,
        total,
        status: "completed",
      }).select("id, invoice_number").single();
      if (invErr) throw invErr;

      // 3. Insert line items
      const itemsPayload = cart.map((c) => ({
        invoice_id: invoice.id,
        product_name: c.platform ? `${c.name} (${c.platform})` : c.name,
        price: c.price,
        quantity: c.qty,
      }));
      const { error: itemsErr } = await supabase.from("customer_invoice_items").insert(itemsPayload);
      if (itemsErr) throw itemsErr;

      // 4. Decrement inventory
      const inventoryItems = cart.filter((c) => c.productId);
      if (inventoryItems.length > 0) {
        const ids = inventoryItems.map((c) => c.productId!);
        const { data: currentProducts, error: fetchErr } = await supabase
          .from("products").select("id, inventory_count").in("id", ids);
        if (fetchErr) throw fetchErr;
        await Promise.all((currentProducts || []).map((p) => {
          const ci = inventoryItems.find((c) => c.productId === p.id);
          const newCount = Math.max(0, p.inventory_count - (ci?.qty || 0));
          return supabase.from("products").update({ inventory_count: newCount }).eq("id", p.id);
        }));
      }

      // 5. Print receipt
      printReceipt({
        invoiceNumber: invoice.invoice_number,
        date: new Date(),
        customerName: customerName.trim() || null,
        customerEmail: customerEmail.trim() || null,
        customerPhone: customerPhone.trim() || null,
        items: cart.map((c) => ({
          name: c.platform ? `${c.name} (${c.platform})` : c.name,
          qty: c.qty,
          price: c.price,
        })),
        subtotal,
        tax,
        total,
      });

      toast({ title: "Sale Complete", description: `${invoice.invoice_number} • $${total.toFixed(2)}${customerName ? ` • ${customerName}` : ""}` });
      setCart([]); setSearch("");
      setCustomerName(""); setCustomerPhone(""); setCustomerEmail("");
      setScannedItem(null); setUpcInput("");
    } catch (err: any) {
      toast({ title: "Checkout Error", description: err.message || "Failed to complete sale.", variant: "destructive" });
    }
  };

  const priceLabels: Record<string, string> = {
    loose: "Loose", complete: "Complete", new: "New", boxOnly: "Box Only", manualOnly: "Manual Only",
  };

  return (
    <div className="space-y-4">
      {/* Customer Info */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-primary" />
          <span className="font-mono text-xs font-bold text-foreground tracking-wider">CUSTOMER</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input placeholder="Name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="font-mono text-sm bg-background border-border" />
          <Input placeholder="Phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="font-mono text-sm bg-background border-border" />
          <Input placeholder="Email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="font-mono text-sm bg-background border-border" />
        </div>
      </div>

      {/* UPC Scanner */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Barcode className="w-4 h-4 text-primary" />
          <span className="font-mono text-xs font-bold text-foreground tracking-wider">SCAN UPC</span>
        </div>

        {isScanning && (
          <BarcodeScanner onScan={handleBarcodeScan} isScanning={isScanning} setIsScanning={setIsScanning} />
        )}

        <div className="flex gap-2">
          <Input
            placeholder="Enter or scan UPC..."
            value={upcInput}
            onChange={(e) => setUpcInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookupUPC(upcInput)}
            className="font-mono text-sm bg-background border-border flex-1"
          />
          <Button variant="outline" size="icon" onClick={() => setIsScanning(!isScanning)} className="shrink-0">
            <Camera className="w-4 h-4" />
          </Button>
          <Button onClick={() => lookupUPC(upcInput)} disabled={upcLoading || !upcInput.trim()} className="font-mono text-xs shrink-0">
            {upcLoading ? "..." : "Lookup"}
          </Button>
        </div>

        {/* Scanned Item Result */}
        {scannedItem && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
            <div className="flex gap-3">
              {scannedItem.imageUrl && (
                <img src={scannedItem.imageUrl} alt={scannedItem.name} className="w-16 h-20 object-contain rounded-lg bg-background border border-border" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm font-bold text-foreground">{scannedItem.name}</p>
                {scannedItem.platform && (
                  <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{scannedItem.platform}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {Object.entries(scannedItem.prices).map(([key, val]) =>
                val && key !== "graded" ? (
                  <button
                    key={key}
                    onClick={() => addScannedToCart(key)}
                    className="flex flex-col items-center p-2 rounded-lg border border-border bg-background hover:border-primary/50 hover:bg-primary/5 transition-all active:scale-95"
                  >
                    <span className="font-mono text-[9px] text-muted-foreground uppercase">{priceLabels[key] || key}</span>
                    <span className="font-mono text-xs font-bold text-primary">${val}</span>
                  </button>
                ) : null
              )}
            </div>
            <p className="font-mono text-[10px] text-muted-foreground text-center">Tap a price to add to cart</p>
          </div>
        )}
      </div>

      {/* Manual Product search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search inventory to add..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 font-mono text-sm bg-card border-border" />
      </div>

      {search && (
        <div className="space-y-1 max-h-48 overflow-y-auto rounded-xl border border-border bg-card p-2">
          {filtered?.map((p) => (
            <button
              key={p.id}
              onClick={() => { addToCart(p); setSearch(""); }}
              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="text-left">
                <p className="font-mono text-xs font-semibold text-foreground">{p.name}</p>
                <p className="font-mono text-[10px] text-muted-foreground">{p.platform}</p>
              </div>
              <span className="font-mono text-xs font-bold text-primary">${Number(p.price).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Cart */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-primary" />
          <span className="font-mono text-xs font-bold text-foreground tracking-wider">CART</span>
          <Badge variant="secondary" className="ml-auto font-mono text-[10px]">{cart.length} items</Badge>
        </div>

        {cart.length === 0 ? (
          <div className="py-10 text-center">
            <ShoppingCart className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
            <p className="font-mono text-xs text-muted-foreground">Cart is empty</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs font-semibold text-foreground truncate">{item.name}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">{item.platform && `${item.platform} • `}Qty: {item.qty}{item.productId ? " • IN STOCK" : ""}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.price}
                    onChange={(e) => updateCartPrice(item.id, parseFloat(e.target.value))}
                    className="w-20 h-7 font-mono text-xs bg-background border-border text-primary font-bold px-2"
                  />
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive shrink-0" onClick={() => removeFromCart(item.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total + Checkout */}
        <div className="p-3 border-t border-border space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-muted-foreground">Subtotal</span>
              <span className="font-mono text-xs text-foreground">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-muted-foreground">Sales Tax</span>
              <span className="font-mono text-xs text-foreground">${tax.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-border">
              <span className="font-mono text-sm font-bold text-foreground">TOTAL</span>
              <span className="font-mono text-lg font-bold text-primary">${total.toFixed(2)}</span>
            </div>
          </div>
          <Button onClick={handleCheckout} disabled={cart.length === 0} className="w-full font-mono gap-2">
            <DollarSign className="w-4 h-4" /> Complete Sale
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── CUSTOMERS TAB ────────────────────────────────────
const CustomersTab = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [itemSearch, setItemSearch] = useState("");

  // All customers with invoice aggregates
  const { data: customers, isLoading } = useQuery({
    queryKey: ["bo-customers"],
    queryFn: async () => {
      const { data: custs, error } = await supabase
        .from("customers").select("*").order("name");
      if (error) throw error;
      const { data: invs } = await supabase
        .from("customer_invoices")
        .select("id, customer_id, total, created_at");
      const byCust: Record<string, { count: number; total: number; last: string | null }> = {};
      (invs || []).forEach((inv) => {
        if (!inv.customer_id) return;
        const b = byCust[inv.customer_id] || { count: 0, total: 0, last: null };
        b.count += 1;
        b.total += Number(inv.total || 0);
        if (!b.last || inv.created_at > b.last) b.last = inv.created_at;
        byCust[inv.customer_id] = b;
      });
      return (custs || []).map((c) => ({ ...c, ...(byCust[c.id] || { count: 0, total: 0, last: null }) }));
    },
  });

  // Search by item purchased: returns invoices containing matching items
  const { data: itemMatches } = useQuery({
    queryKey: ["bo-customer-item-search", itemSearch],
    enabled: itemSearch.trim().length > 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_invoice_items")
        .select("product_name, quantity, price, invoice_id, customer_invoices!inner(id, invoice_number, created_at, total, customer_id, customer_name, customer_email, customer_phone)")
        .ilike("product_name", `%${itemSearch.trim()}%`)
        .order("created_at", { ascending: false, referencedTable: "customer_invoices" })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  // Selected customer's receipts
  const { data: receipts } = useQuery({
    queryKey: ["bo-customer-receipts", selectedCustomerId],
    enabled: !!selectedCustomerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_invoices")
        .select("*")
        .eq("customer_id", selectedCustomerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Selected receipt items
  const { data: receiptDetail } = useQuery({
    queryKey: ["bo-receipt", selectedInvoiceId],
    enabled: !!selectedInvoiceId,
    queryFn: async () => {
      const { data: inv } = await supabase.from("customer_invoices").select("*").eq("id", selectedInvoiceId!).single();
      const { data: items } = await supabase.from("customer_invoice_items").select("*").eq("invoice_id", selectedInvoiceId!).order("created_at");
      return { invoice: inv, items: items || [] };
    },
  });

  const deleteCustomer = async (id: string) => {
    const { error } = await supabase.from("customers").delete().eq("id", id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Customer removed" });
    queryClient.invalidateQueries({ queryKey: ["bo-customers"] });
  };

  const filteredCustomers = customers?.filter((c) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q)
    );
  });

  const fmtDate = (s?: string | null) => s ? new Date(s).toLocaleString() : "—";

  return (
    <div className="space-y-4">
      {/* Search bar — by name/email/phone */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <span className="font-mono text-xs tracking-wider text-foreground">CUSTOMER DIRECTORY</span>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 font-mono text-sm bg-background border-border"
          />
        </div>
      </div>

      {/* Search by purchased item */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-primary" />
          <span className="font-mono text-xs tracking-wider text-foreground">SEARCH WHAT THEY BOUGHT</span>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="e.g. Zelda, PS2 controller, Pokemon..."
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            className="pl-9 font-mono text-sm bg-background border-border"
          />
        </div>
        {itemSearch.trim().length > 1 && (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-[10px]">Item</TableHead>
                  <TableHead className="font-mono text-[10px]">Customer</TableHead>
                  <TableHead className="font-mono text-[10px]">Receipt</TableHead>
                  <TableHead className="font-mono text-[10px] text-right">Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(itemMatches || []).length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center font-mono text-xs text-muted-foreground py-6">No matches.</TableCell></TableRow>
                ) : (
                  (itemMatches || []).map((m: any) => (
                    <TableRow key={`${m.invoice_id}-${m.product_name}`} className="cursor-pointer" onClick={() => setSelectedInvoiceId(m.invoice_id)}>
                      <TableCell className="font-mono text-xs">{m.product_name} ×{m.quantity}</TableCell>
                      <TableCell className="font-mono text-xs">{m.customer_invoices?.customer_name || "Walk-in"}</TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground">{m.customer_invoices?.invoice_number}</TableCell>
                      <TableCell className="font-mono text-xs text-right">${Number(m.price).toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Customer list */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono text-[10px]">Name</TableHead>
              <TableHead className="font-mono text-[10px]">Contact</TableHead>
              <TableHead className="font-mono text-[10px] text-right">Receipts</TableHead>
              <TableHead className="font-mono text-[10px] text-right">Spent</TableHead>
              <TableHead className="font-mono text-[10px]">Last</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center font-mono text-xs py-6">Loading...</TableCell></TableRow>
            ) : (filteredCustomers || []).length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center font-mono text-xs text-muted-foreground py-6">No customers yet. Add one via the POS at checkout.</TableCell></TableRow>
            ) : (
              filteredCustomers!.map((c: any) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedCustomerId(c.id)}>
                  <TableCell className="font-mono text-xs font-bold">{c.name || "—"}</TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">
                    <div>{c.email || ""}</div>
                    <div>{c.phone || ""}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-right">{c.count}</TableCell>
                  <TableCell className="font-mono text-xs text-right text-primary">${c.total.toFixed(2)}</TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">{c.last ? new Date(c.last).toLocaleDateString() : "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteCustomer(c.id)}>
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Customer detail dialog: list of receipts */}
      <Dialog open={!!selectedCustomerId} onOpenChange={(o) => !o && setSelectedCustomerId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm tracking-wider">
              {customers?.find((c: any) => c.id === selectedCustomerId)?.name || "Customer"} — RECEIPTS
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-lg border border-border overflow-hidden max-h-[50vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-[10px]">Receipt #</TableHead>
                  <TableHead className="font-mono text-[10px]">Date</TableHead>
                  <TableHead className="font-mono text-[10px] text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(receipts || []).length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center font-mono text-xs text-muted-foreground py-6">No receipts.</TableCell></TableRow>
                ) : (
                  (receipts || []).map((r: any) => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => { setSelectedInvoiceId(r.id); setSelectedCustomerId(null); }}>
                      <TableCell className="font-mono text-xs text-primary">{r.invoice_number}</TableCell>
                      <TableCell className="font-mono text-[10px]">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs text-right">${Number(r.total).toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt detail dialog */}
      <Dialog open={!!selectedInvoiceId} onOpenChange={(o) => !o && setSelectedInvoiceId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm tracking-wider">
              RECEIPT {receiptDetail?.invoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {receiptDetail?.invoice && (
            <div className="space-y-3">
              <div className="text-[10px] font-mono text-muted-foreground space-y-0.5">
                <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(receiptDetail.invoice.created_at)}</div>
                <div>Customer: {receiptDetail.invoice.customer_name || "Walk-in"}</div>
                {receiptDetail.invoice.customer_email && <div>{receiptDetail.invoice.customer_email}</div>}
                {receiptDetail.invoice.customer_phone && <div>{receiptDetail.invoice.customer_phone}</div>}
              </div>
              <div className="rounded-lg border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-mono text-[10px]">Item</TableHead>
                      <TableHead className="font-mono text-[10px] text-right">Qty</TableHead>
                      <TableHead className="font-mono text-[10px] text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receiptDetail.items.map((it: any) => (
                      <TableRow key={it.id}>
                        <TableCell className="font-mono text-xs">{it.product_name}</TableCell>
                        <TableCell className="font-mono text-xs text-right">{it.quantity}</TableCell>
                        <TableCell className="font-mono text-xs text-right">${Number(it.price).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="space-y-1 pt-2 border-t border-border">
                <div className="flex justify-between font-mono text-xs"><span className="text-muted-foreground">Subtotal</span><span>${Number(receiptDetail.invoice.subtotal || 0).toFixed(2)}</span></div>
                <div className="flex justify-between font-mono text-xs"><span className="text-muted-foreground">Tax</span><span>${Number(receiptDetail.invoice.tax || 0).toFixed(2)}</span></div>
                <div className="flex justify-between font-mono text-sm font-bold"><span>TOTAL</span><span className="text-primary">${Number(receiptDetail.invoice.total).toFixed(2)}</span></div>
              </div>
              <Button
                className="w-full font-mono"
                onClick={() => printReceipt({
                  invoiceNumber: receiptDetail.invoice.invoice_number,
                  date: receiptDetail.invoice.created_at,
                  customerName: receiptDetail.invoice.customer_name,
                  customerEmail: receiptDetail.invoice.customer_email,
                  customerPhone: receiptDetail.invoice.customer_phone,
                  items: receiptDetail.items.map((it: any) => ({
                    name: it.product_name,
                    qty: Number(it.quantity) || 1,
                    price: Number(it.price) || 0,
                  })),
                  subtotal: Number(receiptDetail.invoice.subtotal || 0),
                  tax: Number(receiptDetail.invoice.tax || 0),
                  total: Number(receiptDetail.invoice.total || 0),
                })}
              >
                PRINT RECEIPT
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BackOffice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "inventory";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center hover:border-primary/40 transition-colors">
              <ArrowLeft className="w-4 h-4 text-foreground" />
            </button>
            <div>
              <h1 className="text-lg font-mono font-bold text-foreground tracking-tight">
                BACK <span className="text-primary">OFFICE</span>
              </h1>
              <p className="text-[10px] font-mono text-muted-foreground tracking-wider">MANAGEMENT CONSOLE</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="font-mono text-xs" onClick={() => navigate("/")}>
            <ArrowLeft className="w-3 h-3 mr-1" /> Home
          </Button>
        </header>

        <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        {/* Tabs */}
        <Tabs defaultValue={defaultTab} className="space-y-4">
          <TabsList className="w-full bg-card border border-border p-1 rounded-xl">
            <TabsTrigger value="inventory" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <Package className="w-3.5 h-3.5" /> Inventory
            </TabsTrigger>
            <TabsTrigger value="tags" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <Tag className="w-3.5 h-3.5" /> Tags
            </TabsTrigger>
            <TabsTrigger value="pos" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <ShoppingCart className="w-3.5 h-3.5" /> POS
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex-1 font-mono text-[10px] tracking-wider gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-lg">
              <Users className="w-3.5 h-3.5" /> Customers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inventory">
            <InventoryTab />
          </TabsContent>
          <TabsContent value="tags">
            <TagPrintTab />
          </TabsContent>
          <TabsContent value="pos">
            <POSTab />
          </TabsContent>
          <TabsContent value="customers">
            <CustomersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BackOffice;
